<template>
  <b-sidebar
    id="sidebar_user_assigned_video_tours"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0 text-capitalize">
          {{ isAdd ? $t("assigned_video_tours.schedule_video_tours") : $t("actions.save") }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form class="p-2" @submit.prevent="handleSubmit(onSubmit)" @reset.prevent="resetForm">
          <!-- Supervisor-->
          <b-form-group
            class="text-capitalize"
            :label="$t('assigned_video_tours.supervisor')"
          >
            <b-form-input
              :value="currentSupervisorLabel"
              disabled
            />
            <small class="text-muted">
              {{ $t("assigned_video_tours.current_supervisor") }}
            </small>
          </b-form-group>

          <!-- Analyst -->
          <validation-provider
            :name="$t('assigned_video_tours.analyst')"
            rules="required"
            v-slot="{ errors }"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('assigned_video_tours.analyst')"
            >
              <select-paginate
                v-model="analyst"
                :items="analystOptions"
                :class="{ 'is-invalid': errors.length }"
              />
              <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Video Tours -->
          <validation-provider
            :name="$t('assigned_video_tours.video_tours')"
            rules="required"
            v-slot="{ errors }"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('assigned_video_tours.video_tours')"
            >
              <v-select
                v-model="videoTourIds"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="videoTourOptions"
                multiple
                :reduce="(val) => val.value"
                append-to-body
                :calculate-position="withPopper"
                :class="{ 'is-invalid': errors.length }"
              />
              <b-form-invalid-feedback class="d-block">
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Init date -->
          <validation-provider
            :name="$t('assigned_video_tours.init_date')"
            rules="required"
            v-slot="{ errors }"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('assigned_video_tours.init_date')"
            >
              <b-form-input
                v-model="itemData.init_date"
                type="datetime-local"
                :class="{ 'is-invalid': errors.length }"
              />
              <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- End date -->
          <validation-provider
            :name="$t('assigned_video_tours.end_date')"
            rules="required"
            v-slot="{ errors }"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('assigned_video_tours.end_date')"
            >
              <b-form-input
                v-model="itemData.end_date"
                type="datetime-local"
                :class="{ 'is-invalid': errors.length }"
              />
              <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
              :disabled="isSaving"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.save") }}
            </b-button>

            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
              :disabled="isSaving"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { ref, computed } from "@vue/composition-api";
import { required } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import axios from "@/libs/axios";
import moment from "moment-timezone";
import vSelect from "vue-select";
import popperHelper from "@/libs/popper-helper";
import { decryptData } from "@/utils/decryptUserDataUtils";

import SelectPaginate from "../components/SelectPaginate.vue";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    ValidationProvider,
    ValidationObserver,
    SelectPaginate,
    vSelect,
  },
  directives: { Ripple },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      default: () => ({}),
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
    };
  },
  setup(props, { emit }) {
    const toast = useToast();
    const isSaving = ref(false);
    const analystOptions = ref([]);

    const currentUser = JSON.parse(decryptData(window.localStorage.userData));

    const videoTours = ref([]);
    const getVideos = async () => {
      try {
        const { data } = await axios.get("v1/video-tours", { params: { per_page: 500 } });
        videoTours.value = data.data;
      } catch (error) {
        console.error(error);
      }
    };
    getVideos()
    const videoTourOptions = computed(() =>
      videoTours.value.map((tour) => ({
        value: tour.id,
        label: tour.name
      }))
    );

    const buildUserOptionLabel = (user) => {
      const parts = [user?.name, user?.user, user?.email].filter(Boolean);
      return parts.join(" | ");
    };

    const currentSupervisorLabel = computed(() =>
      buildUserOptionLabel(currentUser)
    );

    const loadAnalystOptions = async () => {
      try {
        const { data } = await axios.get("v1/user_assigned_video_tours/getAnalysts");
        const list = data?.data || [];

        analystOptions.value = list.map((user) => ({
          value: user.id,
          label: buildUserOptionLabel(user),
        }));
      } catch (e) {
        analystOptions.value = [];
      }
    };

    const blankData = {
      id: null,
      init_date: null,
      end_date: null,
    };

    const analyst = ref(null);
    const videoTourIds = ref([]);
    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      analyst.value = null;
      videoTourIds.value = [];
    };

    const toInputDatetimeLocal = (value) => {
      if (!value) return null;
      return moment(value).format("YYYY-MM-DDTHH:mm");
    };

    const findAnalystOption = (userLike) => {
      if (!userLike) return null;

      const id = userLike.id || userLike.analyst_user_id || userLike.value;
      const match = analystOptions.value.find((u) => Number(u.value) === Number(id));

      if (match) return match;

      return id
        ? {
            value: id,
            label: buildUserOptionLabel(userLike) || `#${id}`,
          }
        : null;
    };

    const loadData = async () => {
      await loadAnalystOptions();

      if (!props.isAdd && props.itemEdit && props.itemEdit.id) {
        const row = props.itemEdit;

        itemData.value.id = row.id;
        itemData.value.init_date = toInputDatetimeLocal(row.init_date);
        itemData.value.end_date = toInputDatetimeLocal(row.end_date);

        analyst.value = row.analyst
          ? findAnalystOption(row.analyst)
          : row.analyst_user_id
            ? findAnalystOption({ id: row.analyst_user_id })
            : null;

        videoTourIds.value = Array.isArray(row.video_tours)
          ? row.video_tours.map((s) => s.id)
          : [];
      } else {
        resetData();
      }
    };

    const onSubmit = () => {
      if (!analyst.value || !videoTourIds.value.length) return;

      const payload = {
        analyst_user_id: analyst.value.value,
        supervisor_user_id: currentUser.id,
        video_tour_ids: videoTourIds.value,
        init_date: itemData.value.init_date,
        end_date: itemData.value.end_date,
        scheduled: 1,
      };

      isSaving.value = true;

      let dispatch;
      if (props.isAdd) {
        dispatch = store.dispatch("user_assigned_video_tour/add", payload);
      } else {
        dispatch = store.dispatch("user_assigned_video_tour/edit", {
          id: itemData.value.id,
          params: payload,
        });
      }

      dispatch
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title:
                response?.message ||
                (props.isAdd
                  ? "Registro programado creado"
                  : "Registro programado actualizado"),
              icon: "CheckIcon",
              variant: "success",
            },
          });

          emit("refetch-data");
          emit("update:is-sidebar-active", false);
        })
        .catch((err) => {
          const msg =
            err?.response?.data?.message || "Error guardando la programación";

          toast({
            component: ToastificationContent,
            props: {
              title: msg,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          isSaving.value = false;
        });
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

    return {
      withPopper: popperHelper.withPopper,
      analystOptions,
      videoTourOptions,
      currentSupervisorLabel,
      analyst,
      videoTourIds,
      itemData,
      onSubmit,
      loadData,
      refFormObserver,
      getValidationState,
      resetForm,
      isSaving,
    };
  },
};
</script>
